import type { NarrativeSet } from '@/lib/live/narrative'

/** Colori delle due squadre: gli stessi del grafico dell'andamento. */
const CASA = 'var(--league-brand)'
const OSPITI = '#e07840'

/**
 * Le righe della cronaca, disposte a **timeline**.
 *
 * Ogni azione ha: un pallino sul binario (colore della squadra che l'ha fatta —
 * gli stessi colori della curva sopra), il **punteggio dopo l'azione** in
 * evidenza, l'azione con la squadra, e le note di contesto. In coda l'orario, che
 * serve solo a chi lo cerca.
 */
function Righe({
  set,
  homeTeam,
  awayTeam,
  homeColor = CASA,
  awayColor = OSPITI,
}: {
  set: NarrativeSet
  homeTeam: string
  awayTeam: string
  homeColor?: string
  awayColor?: string
}) {
  const colore = (side: 'home' | 'away' | null) =>
    side === 'home' ? homeColor : side === 'away' ? awayColor : 'var(--league-muted)'

  return (
    <ol className="mt-2">
      {set.entries.map((entry) => {
        // Le righe che contano — set chiuso, palla set o palla match annullata —
        // si vedono anche scorrendo: fondo leggermente marcato e pallino pieno.
        const decisiva =
          entry.closesSet || entry.notes.some((nota) => nota.includes('palla set') || nota.includes('palla match'))
        return (
          <li
            key={entry.id}
            className={`grid grid-cols-[0.9rem_4.2rem_minmax(0,1fr)] items-baseline gap-x-2 rounded-md py-1 pr-1.5 sm:grid-cols-[0.9rem_4.2rem_minmax(0,1fr)_3rem] ${
              decisiva ? 'bg-[color:var(--league-surface-alt)]' : ''
            }`}
          >
            {/* Il binario: una linea per tutta la durata del set, con un pallino
                per azione. */}
            <span className="relative flex h-full justify-center self-stretch">
              <span aria-hidden className="absolute inset-y-0 w-px bg-[color:var(--league-line)]" />
              <span
                aria-hidden
                className="relative mt-[0.4rem] h-2 w-2 shrink-0 rounded-full ring-2 ring-[color:var(--league-surface)]"
                style={{ backgroundColor: colore(entry.side) }}
              />
            </span>

            {/* Il punteggio dopo l'azione: è l'ancora della riga, quindi viene
                prima del testo. Il numero della squadra che ha fatto il punto sta
                nel suo colore. */}
            <span className="text-right text-[13px] font-bold tabular-nums leading-snug">
              <span style={{ color: entry.side === 'home' ? homeColor : 'var(--league-ink)' }}>
                {entry.score.home}
              </span>
              <span className="text-[color:var(--league-muted)]">-</span>
              <span style={{ color: entry.side === 'away' ? awayColor : 'var(--league-ink)' }}>
                {entry.score.away}
              </span>
            </span>

            <span className="min-w-0 text-[13px] leading-snug text-[color:var(--league-ink)]">
              <span className="font-medium">{entry.label}</span>
              {entry.side && (
                <span className="font-semibold" style={{ color: colore(entry.side) }}>
                  {' '}
                  {entry.side === 'home' ? homeTeam : awayTeam}
                </span>
              )}
              {entry.notes.length > 0 && (
                <span className="ml-1.5 inline-flex flex-wrap gap-1 align-middle">
                  {entry.notes.map((nota) => (
                    <span
                      key={nota}
                      className="rounded-full bg-[color:var(--league-surface)] px-2 py-0.5 text-[11px] font-semibold text-[color:var(--league-muted)] ring-1 ring-[color:var(--league-line)]"
                    >
                      {nota}
                    </span>
                  ))}
                </span>
              )}
            </span>

            <time className="hidden text-right text-[11px] tabular-nums text-[color:var(--league-muted)] sm:block">
              {new Date(entry.at).toLocaleTimeString('it-IT', {
                timeZone: 'Europe/Rome',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </time>
          </li>
        )
      })}
    </ol>
  )
}

/**
 * La **cronaca di un set**: l'intestazione dice che cos'è (l'andamento del set,
 * azione per azione) e si apre con un clic; dentro, la timeline.
 *
 * Vive dentro la card del set, sotto il grafico dell'andamento (`SetFlowBlock` nel
 * match center, la card del set nella pagina live): è lo stesso set raccontato
 * scambio per scambio. Il richiudibile serve perché un set sono ~45 righe.
 */
export function SetNarrative({
  set,
  homeTeam,
  awayTeam,
  titolo,
  open = false,
  mostraPunteggio = true,
  homeColor,
  awayColor,
  className = '',
}: {
  set: NarrativeSet | undefined | null
  homeTeam: string
  awayTeam: string
  /** Intestazione: dentro la card del set basta «Cronaca del set». */
  titolo?: string
  open?: boolean
  /** Il parziale è già scritto sopra (card del set): qui si può omettere. */
  mostraPunteggio?: boolean
  homeColor?: string
  awayColor?: string
  className?: string
}) {
  if (!set || set.entries.length === 0) return null

  return (
    <details open={open} className={`group ${className}`}>
      <summary className="-mx-1 flex cursor-pointer list-none items-center justify-between gap-3 rounded-lg border border-[color:var(--league-line)] bg-[color:var(--league-surface-alt)] px-2.5 py-2 transition hover:border-[color:var(--league-brand)] [&::-webkit-details-marker]:hidden">
        <span className="flex min-w-0 flex-wrap items-baseline gap-x-2">
          <span className="league-kicker text-[color:var(--league-brand)]">
            {titolo ?? `${set.set}° set`}
          </span>
          <span className="text-[12px] text-[color:var(--league-muted)]">
            punto per punto · {set.entries.length} azioni
            {set.inProgress ? ' · in corso' : ''}
          </span>
        </span>
        <span className="flex shrink-0 items-baseline gap-2">
          {mostraPunteggio && (
            <span className="text-[15px] font-bold tabular-nums text-[color:var(--league-brand)]">
              {set.finalScore ? `${set.finalScore.home}-${set.finalScore.away}` : '—'}
            </span>
          )}
          {mostraPunteggio && set.winner && (
            <span className="hidden text-[11px] font-semibold text-[color:var(--league-muted)] sm:inline">
              {set.winner === 'home' ? homeTeam : awayTeam}
            </span>
          )}
          {/* Il segno che si apre: ruota quando la cronaca è aperta. */}
          <span
            aria-hidden
            className="text-[11px] text-[color:var(--league-muted)] transition-transform group-open:rotate-180"
          >
            ▼
          </span>
        </span>
      </summary>

      <Righe set={set} homeTeam={homeTeam} awayTeam={awayTeam} homeColor={homeColor} awayColor={awayColor} />
    </details>
  )
}

/**
 * La cronaca di tutta la partita in una card sola: la usa la pagina
 * `/live/<sessione>`, dove il grafico dei set è in una sezione a parte.
 *
 * Presentazionale e senza stato (i set si aprono con `<details>` nativo), così la
 * stessa cronaca rende da client e da server.
 */
export function LiveNarrative({
  sets,
  homeTeam,
  awayTeam,
  kicker = 'Cronaca',
  nota = 'Punto per punto, dagli eventi del live',
  homeColor,
  awayColor,
  className = 'rounded-2xl border border-[color:var(--league-line)] bg-[color:var(--league-surface)] p-4',
}: {
  sets: NarrativeSet[]
  homeTeam: string
  awayTeam: string
  kicker?: string
  /** Riga a destra del titolo (provenienza dei dati). */
  nota?: string
  homeColor?: string
  awayColor?: string
  className?: string
}) {
  if (sets.length === 0) return null

  return (
    <div className={className}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p className="league-kicker">{kicker}</p>
        {nota && <span className="text-[12px] text-[color:var(--league-muted)]">{nota}</span>}
      </div>

      <div className="mt-3 space-y-3">
        {sets.map((set, index) => (
          <SetNarrative
            key={set.set}
            set={set}
            homeTeam={homeTeam}
            awayTeam={awayTeam}
            homeColor={homeColor}
            awayColor={awayColor}
            // Il set più recente (o quello in corso) è aperto; gli altri a richiesta.
            open={index === 0}
            className="border-t border-[color:var(--league-line)] pt-3 first:border-t-0 first:pt-0"
          />
        ))}
      </div>
    </div>
  )
}
